import axios from "axios";
import { store } from "../store/index";
import { clearActiveProject, selectActiveProjectId } from "../store/projectSlice";
import { logout } from "../store/authSlice";

/**
 * Global Axios response interceptor.
 *
 * 403 project_revoked  → clear active project, redirect to /dashboard so the
 *                        user picks a project again (their access was revoked
 *                        while they were mid-session).
 *
 * 403 module_forbidden → redirect to /access-denied so the user sees a clear
 *                        "you don't have access" message.
 *
 * 403 account_disabled → a super-admin deactivated this account; log out and
 *                        redirect to /login immediately.
 *
 * All other errors are re-thrown so per-component catch handlers still fire.
 */
export const setupAxiosInterceptors = () => {
  axios.interceptors.request.use((config) => {
    // A caller that explicitly set x-project-id on this request (e.g. Punch.jsx
    // letting a user punch for a specific assigned project, or WorkplaceSettings
    // configuring a project other than the globally active one) wins over the
    // ambient active-project header — only fall back to it when unset.
    const hasExplicitProjectHeader =
      config.headers &&
      (config.headers["x-project-id"] || config.headers["X-Project-Id"]);
    const projectId = selectActiveProjectId(store.getState());
    if (projectId && !hasExplicitProjectHeader) {
      config.headers = config.headers || {};
      config.headers["x-project-id"] = projectId;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const reason = error?.response?.data?.reason;

      if (status === 403) {
        if (reason === "account_disabled") {
          store.dispatch(logout());
          window.location.href = "/login";
          return new Promise(() => {});
        }

        if (reason === "project_revoked") {
          store.dispatch(clearActiveProject());
          window.location.href = "/dashboard";
          return new Promise(() => {}); // suppress further error propagation
        }

        if (reason === "module_forbidden") {
          window.location.href = "/access-denied";
          return new Promise(() => {});
        }
      }

      return Promise.reject(error);
    },
  );
};
